import { Component, Input, OnInit, inject } from '@angular/core';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { D3Helper, FuelKpiRequest, MultiLineChartComponent } from '../../../../_shared';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { formatCostDifference, formatDropdownValue, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { format } from 'date-fns';
import { DateInterval } from '../../../../../_shared/enums';
import { GraphTrendIconComponent } from "../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component";
import { CommonModule } from '@angular/common';
import { FuelConsumptionService } from '../../../services/fuel-consumption.service';
import { FuelInsightsRequest } from '../../../models/fuel-insights-request.model';
import { EmptyStateComponent } from "../../../../../_shared/components/states/empty-state/empty-state.component";
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-fuel-consumption-widget-graph',
  standalone: true,
  imports: [CommonModule, MultiLineChartComponent, GraphTrendIconComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './fuel-consumption-widget-graph.component.html',
  styleUrl: './fuel-consumption-widget-graph.component.scss'
})
export class FuelConsumptionWidgetGraphComponent extends BaseWidgetComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  @Input() widgetExpandable: boolean = true;

  override isLoading = true;

  public cost = 0;
  protected lineChartData: any[] = [];
  protected xAxisConfig: any = { tickCount: 12 };
  private insightsData: any[] = [];
  private globalInsightsData: any[] = [];
  currencyCode: string = '';
  currencySymbol: string = '';
  protected lineConfigs = [
    { key: 'MovingFuelUsage', label: this.transloco.translate('fuelConsumption.graph.usage'), color: '#EE2737', legend: this.transloco.translate('fuelConsumption.graph.movingFuelUsage', { unit: 'ℓ' }) },
    { key: 'GlobalAvgConsumption', label: this.transloco.translate('fuelConsumption.graph.avg'), color: '#3873C9', legend: this.transloco.translate('fuelConsumption.graph.globalAvgConsumption', { unit: 'ℓ' }) }
  ];

  dropdownOptions: DropdownOption[] = [];
  selectedUnit = MeasurementTypes.FuelEconomy.toString();
  yAxisConfig = {};

  constructor(
    filterService: FilterService,
    private fuelConsumptionService: FuelConsumptionService,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
  }

  ngOnInit(): void {
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);

    this.dropdownOptions = [
      { label: this.transloco.translate('fuelDashboard.units.litres'), value: MeasurementTypes.Volume.toString(), unit: 'ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.kmPerLitres'), value: MeasurementTypes.FuelEconomy.toString(), unit: 'km/ℓ' },
      { label: this.transloco.translate('fuelDashboard.units.litresPer100Km'), value: MeasurementTypes.FuelConsumption.toString(), unit: 'ℓ/100km' },
      { label: this.transloco.translate('fuelDashboard.units.costPerKm', { currency: this.currencyCode }), value: MeasurementTypes.CostPerUnit.toString(), unit: '/km' }
    ];
    this.yAxisConfig = {
      suffix: this.selectedUnitOption.unit,
      yTickFormat: (d: number) => `${D3Helper.formatCompactNumber(d)} ${this.selectedUnitOption.unit}`,
      ticks: [60, 50, 40, 30, 20, 10, 0]
    }
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.loadAllFuelData();
  }

  formatDifference(): string {
    return formatCostDifference(this.difference, this.selectedUnit, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  formatValue(value: number): string {
    return formatDropdownValue(value, this.selectedUnit, this.dropdownOptions, getEnumFromString, this.currencySymbol);
  }

  formatPercentChange(): string {
    if (this.percentageDifference != null) {
      const sign = this.percentageDifference > 0 ? '+' : '';
      return `${sign}${this.percentageDifference.toFixed(2)}%`;
    }
    return 'N/A';
  }

  isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  getArrowIcon(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0
        ? 'assets/images-fuel/yellowArrowIcon.svg'
        : 'assets/images-fuel/greenArrowIcon.svg';
    }
    return 'assets/images-fuel/yellowArrowIcon.svg';
  }

  getPercentChangeClass(): string {
    if (this.percentageDifference != null) {
      return this.percentageDifference >= 0 ? 'percent-increase' : 'percent-decrease';
    }
    return '';
  }

  get selectedUnitOption(): DropdownOption {
    return this.dropdownOptions.find(option => option.value === this.selectedUnit) || this.dropdownOptions[0];
  }

  onOptionClick(option: DropdownOption) {
    this.selectedUnit = option.value;

    this.yAxisConfig = {
      suffix: option.unit,
      prefix: option.value === MeasurementTypes.CostPerUnit.toString() ? this.currencySymbol : '',
      yTickFormat: (d: number) => option.value === MeasurementTypes.CostPerUnit.toString() ?
        `${this.currencySymbol}${D3Helper.formatCompactNumber(d)} ${option.unit}` : `${D3Helper.formatCompactNumber(d)} ${option.unit}`,
      ticks: [60, 50, 40, 30, 20, 10, 0]
    };
    this.lineConfigs[0].legend = this.transloco.translate('fuelConsumption.graph.movingFuelUsage', { unit: option.unit });
    this.lineConfigs[1].legend = this.transloco.translate('fuelConsumption.graph.globalAvgConsumption', { unit: option.unit });
    this.loadAllFuelData();
  }

  get fuelMeasurementType(): MeasurementTypes {
    return getEnumFromString(this.selectedUnit);
  }

  private async loadAllFuelData(): Promise<void> {
    this.isLoading = true;
    this.lineChartData = [];

    try {
      this.cancelSubject.next();
      await Promise.all([
        this.loadFuelConsumptionSummaryData(),
        this.loadFuelInsights(),
        this.loadGlobalFuelInsights(),
      ])
      this.loadGraphData();
      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private async loadFuelConsumptionSummaryData(): Promise<void> {
    const request: FuelKpiRequest = {
      fuelMeasurementType: getEnumFromString(this.selectedUnit),
      accountIds: this.widgetFilter.accountIds,
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
      previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
      previousTimeTo: this.widgetFilter.previousCycleEndDate!
    };

    const response = await this.fuelConsumptionService.getFuelConsumptionSummary(request, this.cancelSubject);
    if (response.data) {
      this.value = response.data.totalValue;
      this.percentageDifference = response.data.percentageChange;
      this.cost = response.data.totalCost;
      this.difference = response.data.difference;
    }
  }

  private async loadFuelInsights(): Promise<void> {
    await this.loadInsightsData('moving');
  }

  private async loadGlobalFuelInsights(): Promise<void> {
    await this.loadInsightsData('global');
  }

  private async loadInsightsData(type: 'moving' | 'global'): Promise<void> {
    const request: FuelInsightsRequest = {
      fuelMeasurementType: getEnumFromString(this.selectedUnit),
      interval: this.widgetFilter?.interval,
      accountIds: this.widgetFilter.accountIds,
      timeFrom: this.widgetFilter.startDate!,
      timeTo: this.widgetFilter.endDate!,
    };

    const selectedOption = getEnumFromString(this.selectedUnit);
    let response: any;

    if (type === 'moving') {
      switch (selectedOption) {
        case MeasurementTypes.Volume:
          response = await this.fuelConsumptionService.getMovingFuelVolumeInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.FuelEconomy:
          response = await this.fuelConsumptionService.getMovingFuelEconomyInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.FuelConsumption:
          response = await this.fuelConsumptionService.getMovingFuelConsumptionInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.CostPerUnit:
          response = await this.fuelConsumptionService.getMovingFuelCostPerUnit(request, this.cancelSubject);
          break;
      }
    } else {
      switch (selectedOption) {
        case MeasurementTypes.Volume:
          response = await this.fuelConsumptionService.getGlobalVolumeInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.FuelEconomy:
          response = await this.fuelConsumptionService.getGlobalFuelConsumptionEconomyInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.FuelConsumption:
          response = await this.fuelConsumptionService.getGlobalConsumptionInsights(request, this.cancelSubject);
          break;
        case MeasurementTypes.CostPerUnit:
          response = await this.fuelConsumptionService.getGlobalCostPerUnit(request, this.cancelSubject);
          break;
      }
    }

    if (response?.data) {
      type === 'moving'
        ? this.insightsData = response.data
        : this.globalInsightsData = response.data;
    }
  }

  private loadGraphData(): void {
    this.processInsights(this.insightsData, this.globalInsightsData);
  }

  private processInsights(movingData: any[], globalData: any[]): void {
    this.lineChartData = [];

    this.lineChartData = movingData.map((movingItem, index) => {
      const timestamp = new Date(movingItem.timeSeries || movingItem.date || movingItem.timestamp);
      const globalItem = globalData[index];

      return {
        date: this.createChartDateString(timestamp, index),
        MovingFuelUsage: movingItem.totalValue ?? 0,
        GlobalAvgConsumption: globalItem?.totalValue ?? 0,
        originalTimestamp: timestamp,
        displayLabel: this.is24HourView()
          ? this.formatToHourly(timestamp)
          : timestamp.toISOString().split('T')[0],
        tooltipDateTime: this.formatTooltipDateTime(timestamp)
      };
    });

    this.lineChartData.sort(
      (a, b) => a.originalTimestamp.getTime() - b.originalTimestamp.getTime()
    );

    this.updateXAxisConfig();
  }

  private is24HourView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Day;
  }

  private isWeekView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Week;
  }

  private isMonthView(): boolean {
    return this.widgetFilter?.interval === DateInterval.Month;
  }

  private formatToHourly(date: Date): string {
    return format(date, 'haaa').replace('AM', 'am').replace('PM', 'pm');
  }

  private formatTooltipDateTime(date: Date): string {
    return format(date, 'd MMM HH:mm');
  }

  private formatXAxisStyle(date: Date): string {
    if (this.is24HourView()) {
      return format(date, 'haaa').replace('AM', 'am').replace('PM', 'pm');
    } else if (this.isWeekView() || this.isMonthView()) {
      return format(date, 'dd MMM');
    } else {
      return format(date, 'MMM');
    }
  }

  private updateXAxisConfig(): void {
    if (this.is24HourView()) {
      this.xAxisConfig = {
        tickCount: 12,
        isHourlyView: true,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    } else if (this.isWeekView() || this.isMonthView()) {
      this.xAxisConfig = {
        tickCount: 4,
        isHourlyView: false,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    } else {
      this.xAxisConfig = {
        tickCount: 4,
        isHourlyView: false,
        customFormatter: (date: Date) => this.formatXAxisStyle(date)
      };
    }
  }

  public hasData(): boolean {
    if (!this.lineChartData || this.lineChartData.length === 0) return false;

    return this.lineChartData.some(item => {
      const movingUsage = Number(item['MovingFuelUsage']) || 0;
      const globalAvg = Number(item['GlobalAvgConsumption']) || 0;
      return movingUsage > 0 || globalAvg > 0;
    });
  }

  private createChartDateString(date: Date, index: number): string {
    if (this.is24HourView()) {
      return format(date, "yyyy-MM-dd'T'HH:00:00");
    } else {
      return format(date, 'yyyy-MM-dd');
    }
  }
}